import { useMemo } from "react";

import { GeneralScopeDto, GeneralScopeType } from "@/core/api/generated/v1";

import AccessoryCheckLink from "../Entity/AccessoryCheck/AccessoryCheckLink";
import AssessmentFlowLink from "../Entity/AssessmentFlow/AssessmentFlowLink";
import AssignmentLink from "../Entity/Assignment/AssignmentLink";
import ContractLink from "../Entity/Contract/ContractLink";
import DamageCostEvaluationLink from "../Entity/DamageCostEvaluation/DamageCostEvaluationLink";
import DamageCostEvaluationAggregateLink from "../Entity/DamageCostEvaluationAggregate/DamageCostEvaluationAggregateLink";
import DamageDetectionLink from "../Entity/DamageDetection/DamageDetectionLink";
import DamageDetectionAggregateLink from "../Entity/DamageDetectionAggregate/DamageDetectionAggregateLink";
import MaintenanceLink from "../Entity/Maintenance/MaintenanceLink";
import RepairOperationLink from "../Entity/RepairOperation/RepairOperationLink";
import TenantConnectionLink from "../Entity/TenantConnection/TenantConnectionLink";
import TenantConnectionRequestLink from "../Entity/TenantConnectionRequest/TenantConnectionRequestLink";
import TenantRequestLink from "../Entity/TenantRequest/TenantRequestLink";
import VehicleLink from "../Entity/Vehicle/VehicleLink";
import VisualInspectionLink from "../Entity/VisualInspection/VisualInspectionLink";
import WashLink from "../Entity/Wash/WashLink";
import WheelOperationLink from "../Entity/WheelOperation/WheelOperationLink";
import { BaseEntityLinkInheritableProps } from "../Entity/components/EntityLink/BaseEntityLink";

interface Props extends BaseEntityLinkInheritableProps {
  scope?: GeneralScopeDto;
}

export default function GeneralScopeLink({ scope, ...linkProps }: Props) {
  const identifier = useMemo(() => scope?.identifier, [scope?.identifier]);

  if (!scope || !scope.identifier || !scope.type) {
    return null;
  }

  return (
    <>
      {scope.type === GeneralScopeType.Vehicle && (
        <VehicleLink entity={scope.vehicle} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.Contract && (
        <ContractLink entity={scope.contract} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.AssessmentFlow && (
        <AssessmentFlowLink entity={undefined} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.DamageDetection && (
        <DamageDetectionLink entity={scope.damageDetection} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.DamageDetectionAggregate && (
        <DamageDetectionAggregateLink
          entity={scope.damageDetectionAggregate}
          entityId={identifier}
          {...linkProps}
        />
      )}

      {scope.type === GeneralScopeType.DamageDetectionAggregateItem && (
        <DamageDetectionAggregateLink
          entity={scope.damageDetectionAggregate}
          entityId={identifier}
          {...linkProps}
        />
      )}
      {scope.type === GeneralScopeType.DamageCostEvaluation && (
        <DamageCostEvaluationLink
          entity={scope.damageCostEvaluation}
          entityId={identifier}
          {...linkProps}
        />
      )}
      {scope.type === GeneralScopeType.DamageCostEvaluationAggregate && (
        <DamageCostEvaluationAggregateLink
          entity={scope.damageCostEvaluationAggregate}
          entityId={identifier}
          {...linkProps}
        />
      )}
      {scope.type === GeneralScopeType.DamageCostEvaluationAggregateItem && (
        <DamageCostEvaluationAggregateLink
          entity={scope.damageCostEvaluationAggregate}
          entityId={identifier}
          {...linkProps}
        />
      )}
      {scope.type === GeneralScopeType.AccessoryCheck && (
        <AccessoryCheckLink entity={scope.accessoryCheck} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.RepairOperation && (
        <RepairOperationLink entity={scope.repairOperation} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.WheelOperation && (
        <WheelOperationLink entity={scope.wheelOperation} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.VisualInspection && (
        <VisualInspectionLink
          entity={scope.visualInspection}
          entityId={identifier}
          {...linkProps}
        />
      )}
      {scope.type === GeneralScopeType.Wash && (
        <WashLink entity={scope.wash} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.Maintenance && (
        <MaintenanceLink entity={scope.maintenance} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.Assignment && (
        <AssignmentLink entity={scope.assignment} entityId={identifier} {...linkProps} />
      )}
      {scope.type === GeneralScopeType.TenantConnection && (
        <TenantConnectionLink
          entity={scope.tenantConnection}
          entityId={identifier}
          {...linkProps}
        />
      )}
      {scope.type === GeneralScopeType.TenantConnectionRequest && (
        <TenantConnectionRequestLink
          entity={scope.tenantConnectionRequest}
          entityId={identifier}
          {...linkProps}
        />
      )}
      {scope.type === GeneralScopeType.TenantRequest && (
        <TenantRequestLink entity={scope.tenantRequest} entityId={identifier} {...linkProps} />
      )}
    </>
  );
}
